import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ptaeService from '../service/ptaeService.mjs';
import { regionLabel } from './PTAERegions.mjs';
import PageShell from '../../components/PageShell.mjs';

/**
 * The finance director's PTAE approval queue, across every region.
 *
 * A request is a batch, not a single code, so importing a region's 178-row chart
 * of accounts is one decision here rather than 178. The summary is shown inline
 * so the decision can be made without opening each code.
 *
 * What is already in effect differs by kind, and the page says so plainly:
 *   - an addition or import is NOT usable until approved
 *   - a suspension IS still usable until approved
 *
 * The email for each request links here with ?request=<id>, and the page
 * scrolls to and marks that request - in whichever tab it now sits, if it has
 * already been decided.
 */

const KIND_COPY = {
  import: { label: 'Chart of accounts import', effect: 'New codes cannot be used until approved.' },
  add: { label: 'New PTAE code', effect: 'This code cannot be used until approved.' },
  edit: { label: 'Edit to a PTAE code', effect: 'The code is unchanged and still usable until you approve this.' },
  suspend: { label: 'Suspension', effect: 'The code is still usable until you approve this.' },
  reactivate: { label: 'Reactivation', effect: 'The code stays suspended until you approve this.' },
};

/** Field names as the requester sees them, so the diff below reads plainly. */
const FIELD_LABEL = {
  ptaeId: 'PTAE code',
  description: 'Description',
  columnA: 'Project-Task.Award',
};

const PTAEApprovals = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deciding, setDeciding] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ptaeService.getChangeBatches(statusFilter);
      setBatches(data.batches || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // The request an email linked to.
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get('request');
  const [lookedUp, setLookedUp] = useState(false);
  const focusRef = useRef(null);
  const scrolled = useRef(false);

  useEffect(() => {
    if (loading || !requestId || lookedUp) return;
    setLookedUp(true);
    if (batches.some(b => String(b.id) === requestId)) return;
    // Not in this tab - most likely already decided, perhaps by another
    // finance director - so open the tab it is in now.
    ptaeService.getChangeBatches('')
      .then((data) => {
        const found = (data.batches || []).find(b => String(b.id) === requestId);
        if (found && found.status !== statusFilter) setStatusFilter(found.status);
      })
      .catch(() => { /* the queue still works without the jump */ });
  }, [loading, requestId, lookedUp, batches, statusFilter]);

  useEffect(() => {
    if (loading || scrolled.current || !focusRef.current) return;
    scrolled.current = true;
    focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [loading, batches]);

  const decide = async (batch, decision, comments = '') => {
    setDeciding(batch.id);
    setNotice(null);
    try {
      const result = await ptaeService.decideChangeBatch(batch.id, decision, comments);
      setNotice({ ok: true, message: result.message });
      setRejectFor(null);
      setRejectReason('');
      await load();
    } catch (err) {
      setNotice({ ok: false, message: err.message });
    } finally {
      setDeciding(null);
    }
  };

  return (
    <PageShell
      title="PTAE Approvals"
      subtitle="Final approval for chart-of-accounts changes, all regions"
      width="5xl"
      actions={
        <>
          {/* The finance director reaches this page from a card on their
              dashboard, so "Dashboard" returns there rather than to home. */}
          <button
            onClick={() => navigate('/ptae-management')}
            className="page-band-btn flex-1 sm:flex-initial"
          >
            All Regions' PTAEs
          </button>
          <button
            onClick={() => navigate('/finance-director')}
            className="page-band-btn"
          >
            Dashboard
          </button>
        </>
      }
    >
        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 mb-4 sm:mb-6 grid grid-cols-3 sm:flex sm:flex-wrap sm:items-center gap-2 sm:gap-3">
          {['pending', 'approved', 'rejected'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`min-w-0 min-h-[44px] sm:min-h-0 px-2 sm:px-3 py-1.5 text-sm rounded-lg capitalize transition-colors ${
                statusFilter === s
                  ? 'bg-stanford-red text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {s}
            </button>
          ))}
          <button
            onClick={load}
            className="col-span-3 sm:ml-auto min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>

        {notice && (
          <div className={`mb-4 sm:mb-6 px-4 py-3 rounded-xl border break-words ${
            notice.ok
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            {notice.message}
          </div>
        )}
        {error && (
          <div className="mb-4 sm:mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl break-words">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4" />
            <p className="text-gray-500">Loading requests…</p>
          </div>
        ) : batches.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-8 sm:p-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center text-2xl">✓</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-1">Nothing {statusFilter}</h3>
            <p className="text-gray-500">
              {statusFilter === 'pending'
                ? 'No chart-of-accounts changes are awaiting your approval.'
                : `No ${statusFilter} requests.`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {batches.map(b => {
              const copy = KIND_COPY[b.kind] || { label: b.kind, effect: '' };
              const s = b.summary || {};
              const linked = !!requestId && String(b.id) === requestId;
              return (
                <div
                  key={b.id}
                  ref={linked ? focusRef : undefined}
                  className={`bg-white rounded-xl shadow-lg p-4 sm:p-5 scroll-mt-24 ${linked ? 'ring-2 ring-stanford-red' : ''}`}
                >
                  <div className="flex sm:flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {copy.label} - {regionLabel(b.region)}
                      </h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Requested by {b.requestedByName || 'unknown'} ·{' '}
                        {b.requestedAt ? new Date(b.requestedAt).toLocaleString('en-US') : '-'}
                      </p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                      b.status === 'pending' ? 'bg-amber-100 text-amber-800'
                      : b.status === 'approved' ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-red-100 text-red-800'
                    }`}>
                      {b.status}
                    </span>
                  </div>

                  {/* What the request contains, so the decision needs no drilling in. */}
                  <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2 text-sm">
                    {b.kind === 'import' ? (
                      <>
                        <Detail label="File" value={s.fileName} />
                        <Detail label="Rows in file" value={s.rowsInFile} />
                        <Detail label="New codes (need approval)" value={s.newCodes} strong />
                        <Detail label="Existing codes refreshed" value={s.refreshedCodes} />
                        <Detail label="Marked inactive in source" value={s.inactiveInSource} />
                        <Detail label="Skipped (unreadable)" value={s.skipped} />
                      </>
                    ) : (
                      <>
                        <Detail label="Code" value={(s.codes || []).join(', ')} strong />
                        <Detail label="Description" value={s.description} />
                        <Detail label="Fund" value={s.fundName} />
                      </>
                    )}
                  </div>

                  {/* An edit is judged on what actually changes, so show the
                      before and after rather than only the resulting row. */}
                  {b.kind === 'edit' && s.changes && Object.keys(s.changes).length > 0 && (
                    <div className="mt-3 rounded-lg border border-gray-200 overflow-hidden">
                      <table className="w-full text-sm responsive-table">
                        <thead className="bg-gray-50 text-gray-500">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium">Field</th>
                            <th className="text-left px-3 py-2 font-medium">Current</th>
                            <th className="text-left px-3 py-2 font-medium">Proposed</th>
                          </tr>
                        </thead>
                        <tbody className="md:divide-y md:divide-gray-100">
                          {Object.entries(s.changes).map(([field, change]) => (
                            <tr key={field} className="last:border-b-0">
                              <td className="px-3 py-2 text-gray-600" data-cell="title">{FIELD_LABEL[field] || field}</td>
                              {/* Strike-through on the value only, so the phone
                                  card's "Current" label is not struck too. */}
                              <td className="px-3 py-2 text-gray-500 break-words md:break-all" data-label="Current">
                                <span className="line-through min-w-0">{change?.from || '-'}</span>
                              </td>
                              <td className="px-3 py-2 font-semibold text-gray-900 break-words md:break-all" data-label="Proposed">
                                <span className="min-w-0">{change?.to || '-'}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {s.changes.ptaeId && (
                        <p className="px-3 py-2 text-xs text-amber-800 bg-amber-50 border-t border-amber-200">
                          Approving this renames the code on every claim already coded
                          against it. Rejecting leaves everything as it is.
                        </p>
                      )}
                    </div>
                  )}

                  {/* The reason is the thing being judged, so it is given its own
                      block rather than sitting in the detail grid. */}
                  {b.reason && (
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <p className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-0.5">
                        Reason given
                      </p>
                      <p className="text-sm text-blue-900 whitespace-pre-wrap">{b.reason}</p>
                    </div>
                  )}

                  {b.kind === 'import' && Array.isArray(s.funds) && s.funds.length > 0 && (
                    <details className="sm:mt-3">
                      <summary className="py-3 sm:py-0 text-sm text-stanford-red cursor-pointer">
                        {s.funds.length} funding programme{s.funds.length === 1 ? '' : 's'}
                      </summary>
                      <ul className="mt-2 text-sm text-gray-600 list-disc list-inside space-y-0.5">
                        {s.funds.map(f => <li key={f}>{f}</li>)}
                      </ul>
                    </details>
                  )}

                  {b.status === 'pending' && copy.effect && (
                    <p className="mt-3 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                      {copy.effect}
                    </p>
                  )}

                  {b.status !== 'pending' && (
                    <p className="mt-3 text-sm text-gray-600">
                      {b.status === 'approved' ? 'Approved' : 'Rejected'} by {b.decidedByName || 'unknown'}
                      {b.decidedAt ? ` on ${new Date(b.decidedAt).toLocaleString('en-US')}` : ''}
                      {b.decisionComments ? ` - ${b.decisionComments}` : ''}
                    </p>
                  )}

                  {b.status === 'pending' && (
                    <div className="mt-4 flex flex-col-reverse sm:flex-row sm:flex-wrap gap-2 sm:justify-end">
                      {rejectFor === b.id ? (
                        <div className="w-full">
                          <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={2}
                            autoFocus
                            placeholder="Why is this being rejected? (sent back to the requester)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stanford-red"
                          />
                          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-2">
                            <button
                              onClick={() => { setRejectFor(null); setRejectReason(''); }}
                              className="min-h-[44px] sm:min-h-0 px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => decide(b, 'reject', rejectReason)}
                              disabled={!rejectReason.trim() || deciding === b.id}
                              title={!rejectReason.trim() ? 'A reason is required' : ''}
                              className="min-h-[44px] sm:min-h-0 px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                            >
                              {deciding === b.id ? 'Saving…' : 'Confirm Rejection'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            onClick={() => { setRejectFor(b.id); setRejectReason(''); }}
                            disabled={deciding === b.id}
                            className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
                          >
                            Reject
                          </button>
                          <button
                            onClick={() => decide(b, 'approve')}
                            disabled={deciding === b.id}
                            className="min-h-[44px] sm:min-h-0 px-5 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                          >
                            {deciding === b.id ? 'Saving…' : '✓ Approve'}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </PageShell>
  );
};

const Detail = ({ label, value, strong }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex gap-2">
      <span className="text-gray-500">{label}:</span>
      {/* min-w-0 + break-words: a long import file name would otherwise push
          the whole page sideways on a phone. */}
      <span className={`min-w-0 break-words ${strong ? 'font-semibold text-gray-900' : 'text-gray-800'}`}>{value}</span>
    </div>
  );
};

export default PTAEApprovals;
